import * as d3 from "d3";
import { useEffect, useRef } from "react";

import { design } from "@/config/design";
import { useNavigate } from "@/hooks/useNavigate";
import { DimensionType } from "@/types/DimensionType";

import { EntrySegment } from "./ClockPieChart";

type ClockPieChartDataProps = {
  entrySegments: EntrySegment[];
  dimensionType: DimensionType;
};

export function ClockPieChartData({ entrySegments, dimensionType }: ClockPieChartDataProps) {
  const navigate = useNavigate();
  const layerRef = useRef<SVGGElement>(null);

  useEffect(() => {
    if (!layerRef.current) return;

    const selection = d3.select(layerRef.current);
    const keyFn = (entrySegment: EntrySegment) => entrySegment.id;

    const arcPath = (entrySegment: EntrySegment) =>
      d3.arc()({
        endAngle: (entrySegment.endHour / 24) * 2 * Math.PI,
        innerRadius: 0,
        outerRadius: design.ClockPieChart.RADIUS,
        startAngle: (entrySegment.startHour / 24) * 2 * Math.PI,
      }) ?? "";

    const arcStartPath = (entrySegment: EntrySegment) => {
      const a = (entrySegment.startHour / 24) * 2 * Math.PI;
      return d3.arc()({ endAngle: a, innerRadius: 0, outerRadius: design.ClockPieChart.RADIUS, startAngle: a }) ?? "";
    };

    // セグメントパス描画
    selection
      .selectAll<SVGPathElement, EntrySegment>("path.segment")
      .data(entrySegments, keyFn)
      .join(
        (enter) =>
          enter
            .append("path")
            .attr(
              "class",
              (entrySegment) =>
                `segment ${dimensionType === "feeling" ? entrySegment.feelingKey : entrySegment.categoryKey}`,
            )
            .attr("data-id", (d) => d.id)
            .attr("data-start-at", (d) => d.startAt)
            .attr("data-end-at", (d) => d.endAt || "")
            .attr("data-name", (d) => d.activityName)
            .attr("data-category", (d) => d.categoryKey)
            .attr("data-feeling", (d) => d.feelingKey)
            .on("click", (_event, entrySegment) => navigate(`/modals/entries/${entrySegment.id}`))
            .attr("d", (entrySegment) => arcStartPath(entrySegment))
            .call((sel) =>
              sel
                .transition()
                .duration(600)
                .attrTween("d", (entrySegment: EntrySegment) => {
                  const from = (entrySegment.startHour / 24) * 2 * Math.PI;
                  const to = (entrySegment.endHour / 24) * 2 * Math.PI;
                  const interpolate = d3.interpolate(from, to);
                  return (t: number) =>
                    d3.arc()({
                      endAngle: interpolate(t),
                      innerRadius: 0,
                      outerRadius: design.ClockPieChart.RADIUS,
                      startAngle: from,
                    }) ?? "";
                }),
            ),
        (update) =>
          update
            .attr(
              "class",
              (entrySegment) =>
                `segment ${dimensionType === "feeling" ? entrySegment.feelingKey : entrySegment.categoryKey}`,
            )
            .attr("data-id", (d) => d.id)
            .attr("data-start-at", (d) => d.startAt)
            .attr("data-end-at", (d) => d.endAt || "")
            .attr("data-name", (d) => d.activityName)
            .attr("data-category", (d) => d.categoryKey)
            .attr("data-feeling", (d) => d.feelingKey)
            .attr("d", (entrySegment) => arcPath(entrySegment)),
        (exit) => exit.remove(),
      );

    // 目盛オーバーレイは ClockPieChartLayout 側へ移動

    // ラベル（1h以上のデータのみ）
    const labelArc = d3
      .arc()
      .innerRadius(design.ClockPieChart.RADIUS * 0.55)
      .outerRadius(design.ClockPieChart.RADIUS * 0.55);

    const filteredData = entrySegments.filter(
      (entrySegment) => (entrySegment.endHour - entrySegment.startHour) * 60 >= 60,
    );

    const position = (entrySegment: EntrySegment) => {
      const endAngle = (entrySegment.endHour / 24) * 2 * Math.PI;
      const startAngle = (entrySegment.startHour / 24) * 2 * Math.PI;
      const [x, y] = labelArc.centroid({
        endAngle,
        innerRadius: design.ClockPieChart.RADIUS * 0.55,
        outerRadius: design.ClockPieChart.RADIUS * 0.55,
        startAngle,
      });
      return `translate(${x}, ${y})`;
    };

    selection
      .selectAll<SVGTextElement, EntrySegment>("text.label")
      .data(filteredData, keyFn)
      .join(
        (enter) =>
          enter
            .append("text")
            .attr("class", "label")
            .attr("data-id", (d) => d.id)
            .attr("data-start-at", (d) => d.startAt)
            .attr("data-end-at", (d) => d.endAt || "")
            .attr("data-name", (d) => d.activityName)
            .attr("data-category", (d) => d.categoryKey)
            .attr("data-feeling", (d) => d.feelingKey)
            .on("click", (_event, entrySegment) => navigate(`/modals/entries/${entrySegment.id}`))
            .attr("transform", (entrySegment) => position(entrySegment))
            .style("text-anchor", "middle")
            .each(function (entrySegment) {
              const textElement = d3.select(this);
              const duration = (entrySegment.endHour - entrySegment.startHour).toFixed(1);
              textElement
                .append("tspan")
                .attr("x", 0)
                .attr("dy", "0em")
                .style("font-weight", "bold")
                .style("font-size", "16px")
                .text(entrySegment.activityName);
              textElement
                .append("tspan")
                .attr("x", 0)
                .attr("dy", "1.2em")
                .style("font-size", "14px")
                .attr("class", "duration")
                .text(`${duration}h`);
            }),
        (update) =>
          update
            .attr("data-id", (d) => d.id)
            .attr("data-start-at", (d) => d.startAt)
            .attr("data-end-at", (d) => d.endAt || "")
            .attr("data-name", (d) => d.activityName)
            .attr("data-category", (d) => d.categoryKey)
            .attr("data-feeling", (d) => d.feelingKey)
            .attr("transform", (entrySegment) => position(entrySegment))
            .each(function (entrySegment) {
              const sel = d3.select(this as SVGTextElement);
              sel.select("tspan.duration").text(`${(entrySegment.endHour - entrySegment.startHour).toFixed(1)}h`);
            }),
        (exit) => exit.remove(),
      );
  }, [entrySegments, dimensionType, navigate]);

  return (
    <g
      ref={layerRef}
      className="data-layer"
    />
  );
}
